import Vue from 'vue';

import {
  formatNumber,
  autoResize,
  alignCells,
  LinkCellRenderer,
  getNodeChildDetails,
  addGroupingInResponseData
} from '@/helpers/renderHelpers';

import { handlerResponse, sumTotalData, convertTotalDataToObject, addColIdToColumns } from '@/helpers/commonHelpers';
import gridProps from '@/gridProps';
import { addComparatorsToCol } from '@/helpers/grid-helpers';

const totalData = [
  {
    label: 'packages_quantity',
    value: 0
  },
  {
    label: 'amount',
    value: 0,
    key: 'amount'
  },
  {
    label: 'm',
    value: 0,
    key: 'm'
  },
  {
    label: 'm2',
    value: 0,
    key: 'm2'
  },
  {
    label: 'm3',
    value: 0,
    key: 'm3'
  }
];

const groupCellRender = {
  cellRenderer: 'agGroupCellRenderer',
  checkboxSelection: true,
  field: 'grouping',
  headerCheckboxSelection: true,
  headerName: ''
};

const toStockRowStyles = (params) => {
  const rowData = params.data;
  if (rowData) {
    if (params.node.rowPinned) {
      return {'font-weight': 'bold'};
    }
    if (rowData.movement_status === 'A') {
      if (!rowData.ready_to_stock) {
        return {'background': '#ffb2b2'};
      } else if (rowData.raw_type === 'MOVE') {
        return {'background': '#ccf3cc'};
      } else if (rowData.raw_type === 'CLOSE') {
        return {background: '#f7dddd'};
      }
    } else {
      if (rowData.cost === 0 || rowData.cost === rowData.process_price) {
        return {background: '#f7dddd'};
      }
    }
  }
};

const toStockTooltips = (params) => {
  const rowData = params.data;
  if (rowData.movement_status === 'A') {
    if (!rowData.ready_to_stock) {
      if (params.node.parent) {
        return 'In this subpackage not filled required fields for change operation status';
      }
      return 'In this package not filled required fields for change operation status';
    } else if (rowData.raw_type === 'MOVE') {
      return 'This package is on manufacture';
    } else if (rowData.raw_type === 'CLOSE') {
      return 'This package is shipped';
    } else if (rowData.raw_type === 'STOCK') {
      return 'This package is on stock';
    }
  } else {
    if (rowData.cost === 0 || rowData.cost === rowData.process_price) {
      return 'This package has incorrect cost';
    }
  }
};

const cellStyle = (params) => {
  const numericColumns = ['width', 'length', 'thickness'];
  return alignCells(params, numericColumns.includes(params.colDef.field) && 'right');
}

export default {
  namespaced: true,
  state: {
    gridOptions: {
      animateRows: true,
      rowSelection: 'multiple',
      suppressPropertyNamesCheck: true,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        valueFormatter(params) {
          return formatNumber(params.value, null);
        },
        cellStyle
      },
      getRowStyle(params) {
        if (params.node.rowPinned) {
          return {'font-weight': 'bold'};
        }
      },
      onRowDataChanged() {
        if (this.rowData && this.rowData.length) {
          autoResize(this);
        } else {
          this.api && this.api.hideOverlay();
          this.api && this.api.showNoRowsOverlay();
        }
      },
      onGridReady() {
        if (!this.rowData.length) {
          this.api && this.api.showNoRowsOverlay();
        }
      }
    },
    fromStockPackages: [],
    toStockPackages: [],
    fromStockColumns: [],
    toStockColumns: [],
    globalToStockTotalData: _.cloneDeep(totalData),
    globalFromStockTotalData: _.cloneDeep(totalData),
    fromStockTotalData: _.cloneDeep(totalData),
    toStockTotalData: _.cloneDeep(totalData),
    fromStockPinnedData: [],
    toStockPinnedData: [],
    operationGroupings: [],
    groupingsGridColumns: [
      {...groupCellRender, minWidth: 110},
      {headerName: 'thickness_mm', field: 'thickness_str', colId: 'thickness_str',},
      {headerName: 'width_mm', field: 'width_str', colId: 'width_str',},
      {headerName: 'length_mm', field: 'length_str', colId: 'length_str',},
      {headerName: 'quality', field: 'qualities_names', colId: 'qualities_names',},
      {headerName: 'package_nr', field: 'package_nr', colId: 'package_nr',},
      {headerName: 'cost', field: 'cost', colId: 'cost',},
      {headerName: 'price_per_m3', field: 'price_per_m3', colId: 'price_per_m3',}
    ],
    additionalCostsGridColumns: [
      {...groupCellRender, maxWidth: 69, headerCheckboxSelection: false},
      {headerName: 'type', field: 'type', colId: 'type',},
      {headerName: 'name', field: 'name', colId: 'name',},
      {headerName: 'value', field: 'cost_str', colId: 'cost_str',},
      {headerName: 'total', field: 'total', colId: 'total',},
      {headerName: 'partner', field: 'partner_name', colId: 'partner_name', cellRenderer: 'PartnerLink'}
    ],
    labelsColumns: [
      {...groupCellRender, maxWidth: 100},
      {field: 'package_nr', colId: 'package_nr', headerName: 'package_nr'},
      {
        field: 'twl',
        colId: 'twl',
        headerName: 'twl'
      },
      {
        field: 'tree_name',
        colId: 'tree_name',
        headerName: 'wood'
      },
      {
        field: 'profile_name',
        colId: 'profile_name',
        headerName: 'profile'
      },
      {
        field: 'quality_name',
        colId: 'quality_name',
        headerName: 'quality'
      },
      {
        field: 'humidity',
        colId: 'humidity',
        headerName: 'humidity'
      },
      {
        field: 'impr_name',
        colId: 'impr_name',
        headerName: 'impr'
      },
      {
        field: 'wood_certificate_name',
        colId: 'wood_certificate_name',
        headerName: 'certificate'
      },
      {
        field: 'partner_name',
        colId: 'partner_name',
        headerName: 'provider'
      },
      {
        field: 'amount',
        colId: 'amount',
        headerName: 'quantity'
      },
      {
        field: 'serial_nr',
        colId: 'serial_nr',
        headerName: 'nr_in_operation'
      },
      {
        field: 'moved_to_stock_date',
        colId: 'moved_to_stock_date',
        headerName: 'start_date'
      },
      {
        field: 'comment',
        colId: 'comment',
        headerName: 'comment'
      }
    ],
    fixedPrices: [],
    operationCosts: [],
    scores: [],
    documents: [],
    fromStockGridName: '',
    toStockGridName: '',
    isSelectPacket: false
  },
  getters: {
    toStockColumns(state) {
      return state.toStockColumns.map(item => addComparatorsToCol(item));
    },
    fromStockColumns(state) {
      return state.fromStockColumns.map(item => addComparatorsToCol(item));
    },
    toStockGrid(state) {
      return {
        ...state.gridOptions, ...gridProps, ...{
          groupSelectsChildren: true,
          suppressScrollOnNewData: true,
          getNodeChildDetails: getNodeChildDetails,
          getRowStyle(params) {
            return toStockRowStyles(params)
          },
          defaultColDef: {
            ...state.gridOptions.defaultColDef,
            valueFormatter(params) {
              return formatNumber(params.value, null);
            },
            cellStyle,
            tooltipValueGetter(params) {
              return toStockTooltips(params)
            }
          }
        }
      };
    },
    globalGridFromStock(state) {
      return {
        ...state.gridOptions, ...gridProps, ...{
          groupSelectsChildren: true,
          getNodeChildDetails: getNodeChildDetails
        }
      };
    },
    globalGridToStock(state) {
      return {
        ...state.gridOptions, ...gridProps, ...{
          groupSelectsChildren: true,
          getNodeChildDetails: getNodeChildDetails,
          suppressRowClickSelection: true,
          getRowStyle(params) {
            return toStockRowStyles(params)
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value, null);
            },
            cellStyle,
            tooltipValueGetter(params) {
              return toStockTooltips(params)
            }
          }
        }
      };
    },
    fromStockGrid(state) {
      return {
        ...state.gridOptions, ...gridProps, ...{
          groupSelectsChildren: true,
          suppressScrollOnNewData: true,
          getNodeChildDetails: getNodeChildDetails
        }
      };
    },
    groupingsGrid(state) {
      return {...state.gridOptions, ...{groupSelectsChildren: true, getNodeChildDetails: getNodeChildDetails}};
    },
    fixedPriceGrid(state) {
      return {...state.gridOptions, ...{rowSelection: 'single'}};
    },
    operationCostGrid(state) {
      return {...state.gridOptions, ...{rowSelection: 'single'}};
    },
    scoresGrid(state) {
      return {...state.gridOptions}
    },
    additionalCostsGrid(state) {
      return {
        ...state.gridOptions, ...{
          rowSelection: 'single', components: {
            'PartnerLink': LinkCellRenderer('partner_id', '/partners/'),
          }
        }
      };
    },
    labelsGrid(state) {
      return {
        ...state.gridOptions,
        //groupSelectsChildren: true,
        getNodeChildDetails: getNodeChildDetails
      };
    }
  },
  mutations: {
    setFromStockPackages(state, payload) {
      state.fromStockPackages = addGroupingInResponseData(payload.data);
      state.fromStockColumns = addColIdToColumns(payload.used_columns);
      state.fromStockPinnedData = convertTotalDataToObject(payload.total);
      state.fromStockGridName = payload.grid_name;
    },
    setToStockPackages(state, payload) {
      state.toStockPackages = addGroupingInResponseData(payload.data);
      state.toStockColumns = addColIdToColumns(payload.used_columns);
      state.toStockPinnedData = convertTotalDataToObject(payload.total);
      state.toStockGridName = payload.grid_name;
    },
    setGlobalFromStockTotal(state, {data}) {
      state.globalFromStockTotalData = sumTotalData(data, state.globalFromStockTotalData);
    },
    setGlobalToStockTotal(state, {data}) {
      state.globalToStockTotalData = sumTotalData(data, state.globalToStockTotalData);
    },
    setFromStockTotal(state, {data}) {
      state.fromStockTotalData = sumTotalData(data, state.fromStockTotalData);
    },
    setToStockTotal(state, {data}) {
      state.toStockTotalData = sumTotalData(data, state.toStockTotalData);
    },
    setGroupings(state, payload) {
      state.operationGroupings = addGroupingInResponseData(payload);
    },
    setFixedPrices(state, payload) {
      state.fixedPrices = payload;
    },
    setScores(state, payload) {
      state.scores = payload;
    },
    setOperationCosts(state, payload) {
      state.operationCosts = payload;
    },
    setDocumentsList(state, data) {
      state.documents = data;
    },
    setIsSelectedPacket(state, data) {
      state.isSelectPacket = data;
    }
  },
  actions: {
    async loadFromStockPackages(store, operationId) {
      try {
        const data = await Vue.http.get(`woodware/from_stock/${operationId}/data`).then(handlerResponse);
        store.commit('setFromStockPackages', data);
        store.commit('setGlobalFromStockTotal', data);
        store.commit('setFromStockTotal', data);
      } catch (e) {
        console.log(e)
      }
    },
    async loadToStockPackages(store, operationId) {
      try {
        const data = await Vue.http.get(`woodware/to_stock/${operationId}/data`).then(handlerResponse);
        store.commit('setToStockPackages', data);
        store.commit('setGlobalToStockTotal', data);
        store.commit('setToStockTotal', data);
      } catch (e) {
        console.log(e);
      }
    },
    loadDocumentsList(store, operationId) {
      Vue.http.get(`woodware/movements/${operationId}/attachments/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setDocumentsList', data);
        });
    },
    loadOperationGroups(store, movement) {
      const params = {movement};
      Vue.http.get(`woodware/movement_groups/`, {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setGroupings', data);
        });
    },
    loadFixedPrices(store, movement) {
      const params = {movement};
      Vue.http.get(`woodware/prices/fixed_prices/`, {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setFixedPrices', data);
        });
    },
    async loadScores({commit}, movement) {
      const params = {movement};
      try {
        const data = await Vue.http.get(`woodware/prices/packages_scores/`, {params}).then(handlerResponse);
        commit('setScores', data);
      } catch (e) {
        console.log(e)
      }
    },
    loadOperationCosts(store, movement) {
      const params = {movement};
      Vue.http.get(`woodware/prices/process_prices/`, {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setOperationCosts', data);
        });
    },
    setIsSelectedPacket({commit}, data) {
      localStorage.setItem('isSelectPacket', data);
      commit('setIsSelectedPacket', data);
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/manufacture/operations/operation.js